'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import {
  Building2, ShoppingCart, TrendingUp, Award, Landmark, Globe, ShieldCheck, ArrowRight, Eye, EyeOff, Copy, CheckCheck
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/context/RoleContext';
import { ROLE_EMAIL_MAP } from '@/lib/roleConfig';


const roles = [
  {
    id: 'role-sme',
    icon: Building2,
    title: 'SME / Exporter',
    description: 'Access marketplace, certification status, and Islamic finance matching',
    email: 'sme@nusantarahalalfood.com',
    password: 'HalalSME@2026',
    color: 'var(--emerald-bright)',
  },
  {
    id: 'role-buyer',
    icon: ShoppingCart,
    title: 'Buyer / Trade Partner',
    description: 'Browse verified suppliers, request quotations, and track orders',
    email: 'buyer@gulftrade.ae',
    password: 'TradePartner@2026',
    color: 'var(--info)',
  },
  {
    id: 'role-investor',
    icon: TrendingUp,
    title: 'Investor',
    description: 'View Shariah-compliant investment opportunities and due diligence packs',
    email: 'investor@crescentcapital.com',
    password: 'HalalInvest@2026',
    color: 'var(--gold)',
  },
  {
    id: 'role-certifier',
    icon: Award,
    title: 'Certification Body',
    description: 'Manage certificate issuance, reviews, and audit trail updates',
    email: 'certifier@jakim-partner.my',
    password: 'CertBody@2026',
    color: '#A855F7',
  },
  {
    id: 'role-finance',
    icon: Landmark,
    title: 'Islamic Finance Partner',
    description: 'Review SME funding requests and manage financing product matching',
    email: 'finance@amanahislamic.my',
    password: 'IslamicFin@2026',
    color: 'var(--warning)',
  },
  {
    id: 'role-govt',
    icon: Globe,
    title: 'Government / Industry Body',
    description: 'Monitor ecosystem health, compliance metrics, and trade corridor data',
    email: 'official@miti.gov.my',
    password: 'GovtBody@2026',
    color: 'var(--danger)',
  },
  {
    id: 'role-admin',
    icon: ShieldCheck,
    title: 'HalalCham Admin',
    description: 'Full platform access — onboarding, approvals, risk monitoring, reports',
    email: 'admin@halalcham.global',
    password: 'AdminSuper@2026',
    color: 'var(--emerald-bright)',
  },
];

export default function LoginRoleClient() {
  const router = useRouter();
  const { setRole } = useRole();
  const [selectedRole, setSelectedRole] = useState<typeof roles[0] | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const handleRoleSelect = (role: typeof roles[0]) => {
    setSelectedRole(role);
    setEmail(role.email);
    setPassword(role.password);
    setError('');
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole) { setError('Please select a role to continue.'); return; }
    if (email !== selectedRole.email || password !== selectedRole.password) {
      setError('Invalid credentials — use the demo accounts below to sign in');
      return;
    }
    setLoading(true);
    // Backend integration point: POST /api/auth/login with { email, password, role }
    await new Promise((r) => setTimeout(r, 1200));
    const roleId = ROLE_EMAIL_MAP[selectedRole.email];
    if (roleId) setRole(roleId);
    toast.success(`Welcome to HalalCham — signed in as ${selectedRole.title}`);
    const dashboardPaths: Record<string, string> = {
      'role-sme': '/dashboard/sme',
      'role-buyer': '/dashboard/buyer',
      'role-investor': '/dashboard/investor',
      'role-certifier': '/dashboard/certifier',
      'role-finance': '/dashboard/finance',
      'role-govt': '/dashboard/government',
      'role-admin': '/dashboard/admin',
    };
    router.push(dashboardPaths[selectedRole.id] || '/main-dashboard');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--background)' }}
    >
      <div className="flex flex-1">
      {/* Left — Branding */}
      <div
        className="hidden lg:flex flex-col justify-between w-[440px] xl:w-[520px] shrink-0 p-10 relative overflow-hidden"
        style={{ background: 'var(--secondary)', borderRight: '1px solid var(--border)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 30% 70%, rgba(10, 79, 63, 0.2) 0%, transparent 60%)' }}
        />
        <div className="relative flex items-center gap-3">
          <AppLogo src="/assets/images/Halalchamp_png-1783699489068.png" size={40} />
          <span className="font-bold text-lg" style={{ color: 'var(--gold)' }}>HalalCham</span>
        </div>
        <div className="relative space-y-6">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--gold)', letterSpacing: '0.1em' }}>
              Global Halal SuperApp
            </p>
            <h2 className="text-3xl xl:text-4xl font-extrabold leading-tight mb-4">
              One Platform.<br />Infinite Halal Possibilities.
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              Access the full halal economy ecosystem — marketplace, certification, investment, Islamic finance, and trade intelligence — all in one trusted platform.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: '12,480', label: 'Verified SMEs' },
              { value: '38,920', label: 'Active Certificates' },
              { value: '246', label: 'Investment Opportunities' },
              { value: '42+', label: 'Countries Covered' },
            ].map((s) => (
              <div
                key={`loginstat-${s.label}`}
                className="rounded-xl p-3"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <p className="text-xl font-extrabold font-mono-data" style={{ color: 'var(--emerald-bright)' }}>{s.value}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>© 2026 HalalCham Global. All rights reserved.</p>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto scrollbar-thin">
        <div className="w-full max-w-2xl">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-8 justify-center">
            <AppLogo src="/assets/images/Halalchamp_png-1783699489068.png" size={36} />
            <span className="font-bold text-base" style={{ color: 'var(--gold)' }}>HalalCham</span>
          </div>

          <h1 className="text-2xl font-bold mb-2">Select Your Role</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Choose your role to access the relevant dashboard and features.
          </p>

          {/* Role Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {roles.map((role) => {
              const Icon = role.icon;
              const isSelected = selectedRole?.id === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => handleRoleSelect(role)}
                  className="flex items-start gap-3 p-4 rounded-xl text-left transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    background: isSelected ? `${role.color}12` : 'var(--card)',
                    border: `1px solid ${isSelected ? role.color : 'var(--border)'}`,
                    boxShadow: isSelected ? `0 0 12px ${role.color}25` : 'none',
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ background: `${role.color}15`, border: `1px solid ${role.color}30` }}
                  >
                    <Icon size={17} style={{ color: role.color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-tight mb-0.5" style={{ color: isSelected ? role.color : 'var(--foreground)' }}>
                      {role.title}
                    </p>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      {role.description}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Select a role above to auto-fill"
                className="w-full px-4 py-3 rounded-xl text-sm input-field"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm input-field"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-xs px-3 py-2 rounded-lg" style={{ color: 'var(--danger)', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}
            >
              {loading ? (
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
              ) : (
                <>
                  Access Dashboard
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials Table */}
          <div
            className="mt-8 rounded-xl overflow-hidden"
            style={{ border: '1px solid var(--border)' }}
          >
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ background: 'var(--muted)', borderBottom: '1px solid var(--border)' }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
                Demo Credentials
              </p>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(10, 107, 79, 0.2)', color: 'var(--emerald-bright)' }}>
                Click role to auto-fill
              </span>
            </div>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Role', 'Email', 'Password', ''].map((h) => (
                      <th key={`credth-${h}`} className="px-4 py-2 text-left font-semibold uppercase tracking-wide" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.05em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role) => (
                    <tr
                      key={`cred-${role.id}`}
                      className="table-row-hover cursor-pointer"
                      style={{ borderBottom: '1px solid var(--border)' }}
                      onClick={() => handleRoleSelect(role)}
                    >
                      <td className="px-4 py-2.5 font-medium whitespace-nowrap" style={{ color: role.color }}>
                        {role.title}
                      </td>
                      <td className="px-4 py-2.5 font-mono-data" style={{ color: 'var(--secondary-foreground)' }}>
                        {role.email}
                      </td>
                      <td className="px-4 py-2.5 font-mono-data" style={{ color: 'var(--muted-foreground)' }}>
                        {role.password}
                      </td>
                      <td className="px-4 py-2.5">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleCopy(`${role.email}|${role.password}`, role.id); }}
                          className="p-1 rounded transition-colors hover:bg-muted"
                          style={{ color: 'var(--muted-foreground)' }}
                          title="Copy credentials"
                        >
                          {copied === role.id ? <CheckCheck size={13} style={{ color: 'var(--emerald-bright)' }} /> : <Copy size={13} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-center mt-6 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            Back to{' '}
            <Link href="/" className="font-semibold transition-colors hover:text-emerald-bright" style={{ color: 'var(--emerald-bright)' }}>
              HalalCham Homepage
            </Link>
          </p>
        </div>
      </div>
      </div>
      <footer className="w-full border-t py-3 flex items-center justify-center" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        <p className="text-xs tracking-wide" style={{ color: 'rgba(200,210,220,0.55)', letterSpacing: '0.03em' }}>
          © 2026 TigaPri Sdn Bhd. All rights reserved.
        </p>
      </footer>
    </div>
  );
}